import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, MessageSquare, Users, Megaphone, FileText, Bot, Sparkles, Instagram, KanbanSquare,
  CheckSquare, BarChart3, Settings, LogOut, Menu, X, ChevronsLeft, ChevronsRight, Filter, Zap, Search,
  CircleDot, LifeBuoy, ChevronDown, ListOrdered, Link2, Wand2 } from "lucide-react";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/common";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const NAV = [
  { group: "Overview", items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/analytics", label: "Analytics", icon: BarChart3 },
  ] },
  { group: "Engage", items: [
    { to: "/inbox", label: "Inbox", icon: MessageSquare, badge: "unread" },
    { to: "/contacts", label: "Contacts", icon: Users },
    { to: "/segments", label: "Segments", icon: Filter },
  ] },
  { group: "Marketing", items: [
    { to: "/campaigns", label: "Campaigns", icon: Megaphone },
    { to: "/templates", label: "Templates", icon: FileText },
    { to: "/links", label: "Chat links & QR", icon: Link2 },
  ] },
  { group: "Automate", items: [
    { to: "/automation/auto-replies", label: "Auto-replies", icon: Zap },
    { to: "/automation/flows", label: "Chatbots", icon: Bot },
    { to: "/automation/sequences", label: "Sequences", icon: ListOrdered },
    { to: "/automation/ai", label: "AI Assistant", icon: Sparkles },
    { to: "/automation/studio", label: "AI Studio", icon: Wand2 },
    { to: "/automation/instagram", label: "Instagram", icon: Instagram },
  ] },
  { group: "Sales", items: [
    { to: "/pipeline", label: "Pipeline", icon: KanbanSquare },
    { to: "/tasks", label: "Tasks", icon: CheckSquare, badge: "tasks" },
  ] },
];

const PAGE_TITLES = [
  ["/dashboard", "Dashboard"], ["/analytics", "Analytics"], ["/inbox", "Inbox"], ["/contacts", "Contacts"], ["/segments", "Segments"],
  ["/campaigns", "Campaigns"], ["/templates", "Templates"], ["/automation/auto-replies", "Auto-replies"], ["/automation/flows", "Chatbots"],
  ["/automation/ai", "AI Assistant"], ["/automation/studio", "AI Studio"], ["/automation/sequences", "Sequences"], ["/automation/instagram", "Instagram"], ["/links", "Chat links & QR"], ["/pipeline", "Pipeline"], ["/tasks", "Tasks"], ["/settings", "Settings"],
];

function Logo({ compact }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary text-white">
        <svg viewBox="0 0 64 64" className="h-5 w-5" fill="currentColor" aria-hidden>
          <path d="M18 20h7l4.5 15 5-15h6l5 15 4.5-15h7l-8.5 26h-6.5l-4.5-13.5L33 46h-6.5z" />
        </svg>
      </div>
      {!compact && <span className="font-display text-[18px] font-bold tracking-tight text-white">Wappy</span>}
    </div>
  );
}

function NavItem({ item, collapsed, counts, onNavigate }) {
  const count = item.badge ? counts?.[item.badge] : 0;
  const link = (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-3 rounded-xl px-2.5 py-2 text-[13.5px] font-medium transition-colors",
          isActive ? "bg-sidebar-active text-sidebar-active-foreground" : "text-sidebar-foreground hover:bg-white/5 hover:text-white",
          collapsed && "justify-center px-0",
        )
      }
    >
      <item.icon className="h-[18px] w-[18px] shrink-0" />
      {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
      {count > 0 && (
        <span className={cn("rounded-full bg-lime px-1.5 text-[10px] font-bold leading-4 text-sidebar", collapsed && "absolute -right-0.5 -top-0.5")}>
          {count > 99 ? "99+" : count}
        </span>
      )}
    </NavLink>
  );
  if (!collapsed) return link;
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

function Sidebar({ collapsed, setCollapsed, mobile, onClose, counts }) {
  const { user, org, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };
  return (
    <aside className={cn("flex h-full flex-col bg-sidebar text-sidebar-foreground", collapsed ? "w-[68px]" : "w-[248px]")}>
      <div className={cn("flex h-14 items-center border-b border-sidebar-border px-4", collapsed && "justify-center px-0")}>
        <Logo compact={collapsed} />
        {mobile && (
          <button onClick={onClose} className="ml-auto rounded-md p-1 text-sidebar-muted hover:text-white" aria-label="Close menu"><X className="h-5 w-5" /></button>
        )}
      </div>
      <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
        {NAV.map((g) => (
          <div key={g.group}>
            {!collapsed && <p className="mb-1.5 px-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-sidebar-muted">{g.group}</p>}
            <ul className="space-y-0.5">
              {g.items.map((it) => (
                <li key={it.to}><NavItem item={it} collapsed={collapsed} counts={counts} onNavigate={mobile ? onClose : undefined} /></li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="border-t border-sidebar-border p-3">
        <NavItem item={{ to: "/settings", label: "Settings", icon: Settings }} collapsed={collapsed} onNavigate={mobile ? onClose : undefined} />
        <div className={cn("mt-2 flex items-center gap-2.5 rounded-lg px-2 py-2", collapsed && "justify-center px-0")}>
          <Avatar name={user?.name} size="sm" />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-white">{user?.name}</p>
              <p className="truncate text-[11px] text-sidebar-muted">{org?.name}</p>
            </div>
          )}
          {!collapsed && (
            <button onClick={handleLogout} className="rounded-md p-1.5 text-sidebar-muted hover:bg-white/5 hover:text-white" title="Log out" aria-label="Log out">
              <LogOut className="h-4 w-4" />
            </button>
          )}
        </div>
        {!mobile && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="mt-1 flex w-full items-center justify-center gap-2 rounded-lg py-1.5 text-[11px] text-sidebar-muted hover:bg-white/5 hover:text-white"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <><ChevronsLeft className="h-4 w-4" /> Collapse</>}
          </button>
        )}
      </div>
    </aside>
  );
}

function ConnectionChip({ org }) {
  const navigate = useNavigate();
  const wa = org?.whatsapp;
  if (!wa) return null;
  return (
    <button
      onClick={() => navigate("/settings/whatsapp")}
      className={cn(
        "hidden items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium sm:flex",
        wa.connected ? "border-[#B9D3C0] bg-accent text-accent-foreground" : "border-amber-200 bg-amber-50 text-amber-900",
      )}
    >
      <CircleDot className={cn("h-3 w-3", wa.connected ? "text-primary" : "text-amber-500")} />
      {wa.connected ? (wa.display_phone_number || "WhatsApp connected") : "Connect WhatsApp"}
    </button>
  );
}

export default function AppShell({ children }) {
  const { user, org, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("wappy.sidebar") === "collapsed"; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem("wappy.sidebar", collapsed ? "collapsed" : "open"); } catch { /* ignore */ }
  }, [collapsed]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const { data: badge } = useQuery({
    queryKey: ["nav-badges"],
    queryFn: async () => {
      const [c, t] = await Promise.all([
        api.get("/conversations", { params: { page_size: 1, status: "open" } }),
        api.get("/crm/tasks", { params: { assigned: "me" } }),
      ]);
      return { unread: c.data.counts?.unread || 0, tasks: t.data.counts?.overdue || 0 };
    },
    refetchInterval: 30000,
    enabled: !!user,
  });

  const title = useMemo(() => {
    const hit = PAGE_TITLES.find(([p]) => location.pathname.startsWith(p));
    return hit ? hit[1] : "Wappy";
  }, [location.pathname]);

  useEffect(() => { document.title = `${title} · Wappy`; }, [title]);

  return (
    <TooltipProvider>
      <div className="flex h-screen overflow-hidden bg-background">
        <div className="hidden md:block">
          <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} counts={badge} />
        </div>
        {mobileOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
            <div className="absolute inset-y-0 left-0 shadow-pop"><Sidebar collapsed={false} mobile onClose={() => setMobileOpen(false)} counts={badge} /></div>
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background/90 px-4 backdrop-blur sm:px-6">
            <button className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary md:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </button>
            <h2 className="truncate font-display text-[16px] font-bold">{title}</h2>
            <div className="ml-auto flex items-center gap-2">
              <ConnectionChip org={org} />
              <Button variant="ghost" size="icon-sm" className="hidden text-muted-foreground sm:inline-flex" onClick={() => navigate("/contacts")} title="Search contacts">
                <Search className="h-4 w-4" />
              </Button>
              <a href="/api/docs" target="_blank" rel="noreferrer" className="hidden text-muted-foreground hover:text-foreground sm:block" title="API docs">
                <LifeBuoy className="h-4 w-4" />
              </a>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full border border-border bg-card py-0.5 pl-0.5 pr-2 hover:bg-secondary">
                    <Avatar name={user?.name} size="sm" />
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <p className="truncate text-sm font-medium">{user?.name}</p>
                    <p className="truncate text-xs font-normal text-muted-foreground">{user?.email}</p>
                    <p className="mt-1 text-[11px] font-normal capitalize text-muted-foreground">{user?.role} · {org?.name}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/settings/profile")}>Profile & password</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")}>Workspace settings</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate("/login", { replace: true }); }} className="text-destructive">
                    <LogOut className="h-4 w-4" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <main className="min-h-0 flex-1 overflow-y-auto">{children}</main>
        </div>
      </div>
    </TooltipProvider>
  );
}

export function PageContainer({ children, className, wide }) {
  return <div className={cn("mx-auto w-full px-4 py-6 sm:px-6 lg:px-8", wide ? "max-w-[1600px]" : "max-w-7xl", className)}>{children}</div>;
}
